import { AiMessageSteps, AiMessageStepStatusEnum } from "__generated__/graphql"

/**
 * Commerce answers through an MCP tool loop: the message is classified, the shop's tools are
 * listed for that workflow step, and then the model takes rounds — pick tools, read what came
 * back, decide again — until it writes the reply. Read as a flat step list that is unreadable,
 * so this rebuilds the loop: what was asked, which tool ran with which arguments, what it
 * returned, and how the workflow ended.
 */
export const COMMERCE_STEP_NAMES = [
  "COMMERCE_SLOT_EXTRACTION",
  "COMMERCE_PAGE_PRODUCT_RESOLVE",
  "COMMERCE_MARKETPLACE_PRODUCT_RESOLVE",
  "GET_QPIEN_SHOP_PRODUCT_DETAILS",
  "MARKETPLACE_REQUEST",
  "COMMERCE_MCP_LIST_TOOLS",
  "COMMERCE_AGENT_ROUND",
  "COMMERCE_MCP_TOOL_CALL",
  "COMMERCE_WORKFLOW_START",
  "COMMERCE_WORKFLOW_RESUME",
  "COMMERCE_ERROR"
]

/** A card the shopper was actually sent, beside the answer text, as its own message. */
export type CommerceProductCard = {
  productId?: string
  title?: string
  thumbnailUrl?: string
  optionsSummary?: string
  productUrl?: string
  price?: string
}

export type CommerceRecord = {
  title: string
  /** "Renk: Siyah · Numara: 39" — what tells one variant row from the next. */
  options?: string
  price?: string
  availability?: string
  url?: string
}

export type CommerceToolCall = {
  name: string
  args: [string, string][]
  failed: boolean
  durationMs: number
  note?: string
  records: CommerceRecord[]
  resultCount?: number
  result: unknown
}

export type CommerceRound = {
  index: number
  summary?: string
  note?: string
  durationMs: number
  toolCalls: CommerceToolCall[]
}

export type CommerceRun = {
  workflowType?: string
  productQuery?: string
  selection?: string
  pageProduct?: { url?: string; matched: boolean; reason?: string }
  /**
   * The marketplace listing a QUESTION was asked on, resolved against our own catalogue before
   * the pipeline ran. Separate from `pageProduct` because the two answer different questions:
   * that one is "which page is open", this one is "which listing is this about, and did we find
   * it in our catalogue or did we have to call the marketplace".
   */
  marketplaceProduct?: {
    platform?: string
    ref?: string
    matched: boolean
    reason?: string
    shopId?: string
    withdrawn?: boolean
    /** false = ilan bulundu ama şu anda satın alınabilir değil (DRAFT / stok yok). */
    sellable?: boolean
    /** Which source actually supplied the product text. */
    detailSource?: "CATALOG" | "MARKETPLACE_API"
  }
  tools: { count: number; step?: string; names: string[] } | null
  /**
   * The cards dispatched with the answer. Read off the workflow step rather than the answer
   * bubble so runs recorded before the audit stored them still show what was offered.
   */
  productCards: CommerceProductCard[]
  rounds: CommerceRound[]
  errors: string[]
  outcome: { label: string; tone: "success" | "warning" | "error" | "default" }
  durationMs: number
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined
}

const RECORD_LISTS = [
  "products",
  "items",
  "results",
  "variants",
  "candidates",
  "orders",
  "policies"
]

function readPrice(row: Record<string, unknown>): string | undefined {
  const price =
    asObject(row.salePrice) ??
    asObject(row.listPrice) ??
    asObject(row.price) ??
    asObject(asObject(row.offerSummary)?.minPrice)
  if (price) {
    const amount = price.amount ?? price.value
    return amount
      ? [amount, price.currency].filter(Boolean).join(" ")
      : undefined
  }

  return typeof row.price === "number" || typeof row.price === "string"
    ? String(row.price)
    : undefined
}

function readOptions(row: Record<string, unknown>): string | undefined {
  if (!Array.isArray(row.options)) return undefined
  const pairs = row.options
    .map(asObject)
    .map((option) => {
      const name = asText(option?.name)
      const value = asText(option?.value)
      return name && value ? `${name}: ${value}` : undefined
    })
    .filter(Boolean)
  return pairs.length ? pairs.join(" · ") : undefined
}

/** The rows a commerce tool returned, named the way a reader recognizes them. */
export function readCommerceRecords(response: unknown): CommerceRecord[] {
  const root = asObject(response)
  if (!root) return []

  const rows: Record<string, unknown>[] = []
  RECORD_LISTS.forEach((key) => {
    const list = root[key]
    if (Array.isArray(list)) {
      rows.push(
        ...list
          .map(asObject)
          .filter((row): row is Record<string, unknown> => !!row)
      )
    }
  })
  const single = asObject(root.product) ?? asObject(root.order)
  if (single) rows.push(single)

  return rows
    .map((row) => ({
      title:
        asText(row.title) ??
        asText(row.name) ??
        asText(row.productTitle) ??
        (asText(row.orderNumber) ? `#${asText(row.orderNumber)}` : undefined) ??
        asText(row.sku) ??
        "",
      options: readOptions(row),
      price: readPrice(row),
      availability: asText(row.availability) ?? asText(row.status),
      url: asText(row.productUrl) ?? asText(row.url)
    }))
    .filter((row) => !!row.title)
}

function countResult(response: unknown): number | undefined {
  const root = asObject(response)
  if (!root) return undefined

  const list = RECORD_LISTS.map((key) => root[key]).find((value) =>
    Array.isArray(value)
  )
  if (Array.isArray(list)) return list.length

  return typeof root.count === "number" ? root.count : undefined
}

function readArgs(request: unknown): [string, string][] {
  const args = asObject(asObject(request)?.args)
  if (!args) return []

  return Object.entries(args)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
    .map(([key, value]) => [
      key,
      typeof value === "object" ? JSON.stringify(value) : String(value)
    ])
}

/** The workflow's own closing word, taken from the summary the runtime already writes. */
function readOutcome(
  summary: string | undefined,
  hasError: boolean
): CommerceRun["outcome"] {
  const state = summary?.match(/workflow ([A-Z_]+)/)?.[1]

  if (state === "COMPLETED") {
    return { label: "Tamamlandı", tone: "success" }
  }
  if (state === "WAITING_USER_INPUT") {
    return { label: "Müşteri yanıtı bekleniyor", tone: "default" }
  }
  if (state === "FAILED_RECOVERABLE") {
    return { label: "Tamamlanamadı, akış devam etti", tone: "warning" }
  }
  if (state) {
    return { label: state, tone: "warning" }
  }

  return hasError
    ? { label: "Hata aldı", tone: "error" }
    : { label: "Sonuç yok", tone: "default" }
}

function readProductCards(value: unknown): CommerceProductCard[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    const card = asObject(entry)
    if (!card) return []
    const price = asObject(card.price)
    const amount = asText(price?.amount)
    const currency = asText(price?.currency)
    return [
      {
        productId: asText(card.productId),
        title: asText(card.title),
        thumbnailUrl: asText(card.thumbnailUrl),
        optionsSummary: asText(card.optionsSummary),
        productUrl: asText(card.productUrl),
        price: amount ? `${amount} ${currency ?? ""}`.trim() : undefined
      }
    ]
  })
}

export function buildCommerceRun(
  steps: AiMessageSteps[] | undefined
): CommerceRun | null {
  const relevant = [...(steps ?? [])]
    .sort((a, b) => (a.stepList ?? 0) - (b.stepList ?? 0))
    .filter((step) => COMMERCE_STEP_NAMES.includes(String(step.step)))
  if (!relevant.length) return null

  const run: CommerceRun = {
    tools: null,
    productCards: [],
    rounds: [],
    errors: [],
    outcome: { label: "Sonuç yok", tone: "default" },
    durationMs: 0
  }
  let workflowSummary: string | undefined

  relevant.forEach((step) => {
    const name = String(step.step)
    const response = asObject(step.response)
    run.durationMs += step.durationMs ?? 0

    if (name === "COMMERCE_SLOT_EXTRACTION") {
      const choice = asObject(response?.choice)
      run.workflowType = asText(choice?.workflowType) ?? run.workflowType
      run.productQuery = asText(choice?.productQuery) ?? run.productQuery
      const index = choice?.selectedCandidateIndex
      run.selection =
        typeof index === "number" ? `${index}. seçenek` : run.selection
      return
    }

    if (name === "COMMERCE_PAGE_PRODUCT_RESOLVE") {
      run.pageProduct = {
        url: asText(asObject(step.request)?.pageUrl),
        matched: response?.matched === true,
        reason: asText(response?.reason)
      }
      return
    }

    if (name === "COMMERCE_MARKETPLACE_PRODUCT_RESOLVE") {
      const request = asObject(step.request)
      run.marketplaceProduct = {
        platform: asText(request?.platformType),
        ref: asText(request?.sku) ?? asText(request?.productMainId),
        matched: response?.matched === true,
        // A skipped resolution carries its own reason (the platform is not synced, the question
        // named nothing) — without it a run that never asked looks like a run that asked and
        // found nothing.
        reason: asText(response?.reason),
        shopId: asText(response?.shopId),
        withdrawn: response?.listingWithdrawn === true,
        sellable:
          typeof response?.sellable === "boolean"
            ? response.sellable
            : undefined
      }
      return
    }

    // The catalogue read and the live marketplace call are separate steps on purpose: one run
    // answered from our own data must not look like it hit the marketplace API.
    if (
      name === "GET_QPIEN_SHOP_PRODUCT_DETAILS" ||
      name === "MARKETPLACE_REQUEST"
    ) {
      const detailSource =
        name === "GET_QPIEN_SHOP_PRODUCT_DETAILS"
          ? "CATALOG"
          : "MARKETPLACE_API"
      run.marketplaceProduct = {
        matched: false,
        ...run.marketplaceProduct,
        detailSource
      }
      return
    }

    if (name === "COMMERCE_MCP_LIST_TOOLS") {
      const names = Array.isArray(response?.tools)
        ? (response?.tools as unknown[]).map(String)
        : []
      run.tools = {
        count:
          typeof response?.count === "number" ? response.count : names.length,
        step: asText(asObject(step.request)?.workflowStep),
        names
      }
      if (step.status === AiMessageStepStatusEnum.Failed) {
        run.errors.push(asText(step.reason) ?? "Araç listesi alınamadı.")
      }
      return
    }

    if (name === "COMMERCE_AGENT_ROUND") {
      run.rounds.push({
        index: run.rounds.length + 1,
        summary: asText(step.summary),
        note: asText(step.reason),
        durationMs: step.durationMs ?? 0,
        toolCalls: []
      })
      return
    }

    if (name === "COMMERCE_MCP_TOOL_CALL") {
      const call: CommerceToolCall = {
        name: asText(asObject(step.request)?.name) ?? "araç",
        args: readArgs(step.request),
        failed: step.status === AiMessageStepStatusEnum.Failed,
        durationMs: step.durationMs ?? 0,
        note: asText(step.reason),
        records: readCommerceRecords(step.response),
        resultCount: countResult(step.response),
        result: step.response
      }
      const current = run.rounds[run.rounds.length - 1]
      if (current) {
        current.toolCalls.push(call)
        return
      }
      run.rounds.push({
        index: 1,
        durationMs: 0,
        toolCalls: [call]
      })
      return
    }

    if (
      name === "COMMERCE_WORKFLOW_START" ||
      name === "COMMERCE_WORKFLOW_RESUME"
    ) {
      workflowSummary = asText(step.summary) ?? workflowSummary
      const cards = readProductCards(response?.commerceProductCards)
      if (cards.length) run.productCards = cards
      return
    }

    if (name === "COMMERCE_ERROR") {
      run.errors.push(asText(step.reason) ?? "Bilinmeyen commerce hatası.")
    }
  })

  run.outcome = readOutcome(workflowSummary, run.errors.length > 0)
  return run
}
